import { BasePageable } from '../../models/base/base-pageable.model';

type SlimSort = Array<Pick<BasePageable['sort'][number], 'property' | 'direction'>>;

export class BasePageableBuilder {
  private constructor(
    private totalNumberOfElements: number,
    private numberOfElementsPerPage: number,
    private pageIndex: number,
    private sort: SlimSort
  ) {}

  static aBasePageable(): BasePageableBuilder {
    return new BasePageableBuilder(0, 1, 0, []);
  }

  withTotalNumberOfElements(totalNumberOfElements: number): this {
    if (totalNumberOfElements < 0) {
      throw new Error(`Cannot build BasePageable with totalNumberOfElements ${totalNumberOfElements}`);
    }
    this.totalNumberOfElements = totalNumberOfElements;
    return this;
  }

  withNumberOfElementsPerPage(numberOfElementsPerPage: number): this {
    if (numberOfElementsPerPage <= 0) {
      throw new Error(`Cannot build BasePageable with numberOfElementsPerPage ${numberOfElementsPerPage}`);
    }
    this.numberOfElementsPerPage = numberOfElementsPerPage;
    return this;
  }

  withPageIndex(pageIndex: number): this {
    if (pageIndex < 0) {
      throw new Error(`Cannot build BasePageable with pageIndex ${pageIndex}`);
    }
    this.pageIndex = pageIndex;
    return this;
  }

  withSort(sort: SlimSort): this {
    this.sort = sort;
    return this;
  }

  build(): BasePageable {
    const numberOfPages = Math.max(1, Math.ceil(this.totalNumberOfElements / this.numberOfElementsPerPage));
    if (this.pageIndex >= numberOfPages) {
      throw new Error(
        `Cannot build BasePageable with pageIndex ${this.pageIndex} and numberOfElementsPerPage ${this.numberOfElementsPerPage} when totalNumberOfElements is ${this.totalNumberOfElements}`
      );
    }

    const isFirstPage = this.pageIndex === 0;
    const isLastPage = this.pageIndex === numberOfPages - 1;

    const numberOfElementsOnCurrentPage = isLastPage ? this.totalNumberOfElements % this.numberOfElementsPerPage : this.numberOfElementsPerPage;

    const sort = this.sort.map(({ property, direction }) => ({
      property,
      direction,
      ignoreCase: false,
      nullHandling: 'NATIVE',
      ascending: direction === 'ASC',
      descending: direction === 'DESC'
    }));

    return {
      numberOfElements: numberOfElementsOnCurrentPage,
      totalElements: this.totalNumberOfElements,

      size: this.numberOfElementsPerPage,
      totalPages: numberOfPages,
      number: this.pageIndex,

      empty: this.totalNumberOfElements === 0,
      first: isFirstPage,
      last: isLastPage,

      pageable: {
        sort,
        offset: this.pageIndex * this.numberOfElementsPerPage,
        pageNumber: this.pageIndex,
        pageSize: this.numberOfElementsPerPage,
        unpaged: false,
        paged: true
      },
      sort
    };
  }
}
